import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Clock, Download, Feather, Headphones, Sparkles } from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import StickyCTA from "@/components/StickyCTA";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import useSeo from "@/hooks/useSeo";

const WHO_ITS_FOR = [
  "You keep everything moving and appear entirely capable from the outside.",
  "Nothing is technically wrong, but something no longer feels right underneath.",
  "You have been quietly asking, \"is this it, then?\" in ordinary moments.",
  "You are ready to name what has been building — before rushing to fix anything.",
];

const WHAT_YOULL_DO = [
  "Notice the ordinary moments where something feels unexpectedly heavy or empty.",
  "Distinguish functioning from flourishing — and see where the two have quietly diverged.",
  "Trace the accumulation of small decisions that have shaped a life you never chose on purpose.",
  "Count your own experience as data — the signals you have been explaining away.",
  "Name what is no longer working — without the pressure to solve it today.",
];

function OptInForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/lead-magnet/midlife-clarity-guide", { name, email });
      setDownloadUrl(data?.download_url || "/api/midlife-clarity-guide/download");
      setDone(true);
      toast.success("Your guide is ready below.");
      // GA4 event — mirrors the pattern used elsewhere on the site.
      try {
        if (typeof window !== "undefined" && typeof window.gtag === "function") {
          window.gtag("event", "generate_lead", {
            lead_magnet: "midlife_clarity_guide",
            source: "midlife_clarity_guide_landing",
          });
        }
      } catch { /* non-critical */ }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        className="bg-sage/5 border border-sage/20 rounded-2xl p-8 md:p-10 text-center"
        data-testid="midlife-clarity-success"
      >
        <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-sage text-cream mb-4">
          <Check size={20} strokeWidth={1.5} />
        </div>
        <h3 className="font-serif-display text-2xl md:text-3xl text-ink mb-3">
          Your guide is ready.
        </h3>
        <p className="text-ink-soft leading-relaxed max-w-md mx-auto mb-6">
          Thank you for downloading <em className="italic">Something Has to Change: A Midlife Clarity Guide</em>.
          Give yourself around 15 quiet minutes to work through it. There is no pressure to make a decision or
          create a plan &mdash; the guide simply helps you listen more closely to what your own experience may already
          be telling you.
        </p>
        <a
          href={downloadUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-6 py-4 font-medium text-sm transition-all"
          data-testid="midlife-clarity-download-pdf"
        >
          <Download size={16} strokeWidth={1.6} />
          Download the guide (PDF)
        </a>
        <p className="mt-4 text-xs text-ink-soft/70">
          A copy is also on its way to your inbox.
        </p>

        <div className="mt-10 pt-8 border-t border-hairline">
          <div className="eyebrow mb-3 text-sage">You may also enjoy</div>
          <p className="text-ink-soft text-sm max-w-md mx-auto mb-4">
            Pip&rsquo;s <em className="italic">Magnificent Midlife Podcast</em> &mdash; where she explores midlife, menopause,
            identity, relationships and meaningful change.
          </p>
          <Link
            to="/podcast"
            className="inline-flex items-center gap-2 border border-sage text-sage hover:bg-sage hover:text-cream rounded-full px-5 py-3 font-medium text-sm transition-all"
            data-testid="midlife-clarity-podcast-cta"
          >
            <Headphones size={14} strokeWidth={1.8} />
            Listen to the Podcast
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="max-w-lg mx-auto space-y-3"
      data-testid="midlife-clarity-form"
      autoComplete="off"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          required
          type="text"
          name="first_name"
          autoComplete="given-name"
          placeholder="First name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="bg-white border border-hairline rounded-full px-6 py-4 text-ink placeholder-ink-soft/60 focus:outline-none focus:border-sage transition"
          data-testid="midlife-clarity-name"
        />
        <input
          required
          type="email"
          name="email"
          autoComplete="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="bg-white border border-hairline rounded-full px-6 py-4 text-ink placeholder-ink-soft/60 focus:outline-none focus:border-sage transition"
          data-testid="midlife-clarity-email"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-sage hover:bg-sage-dark text-cream rounded-full px-6 py-4 font-medium tracking-wide transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2"
        data-testid="midlife-clarity-submit"
      >
        {loading ? "Sending…" : (
          <>
            Get The Free Clarity Guide
            <ArrowRight size={16} strokeWidth={1.8} />
          </>
        )}
      </button>
    </form>
  );
}

export default function MidlifeClarityGuide() {
  useSeo({
    title: "Something Has to Change — A Midlife Clarity Guide · Free · Pip Bennett",
    description:
      "A free, 15-minute reflection for the moment when everything looks fine on paper but no longer feels right. Notice what has been building, distinguish functioning from flourishing, and begin naming what needs to change.",
    canonical: "https://pipbennett.com/midlife-clarity-guide",
  });

  return (
    <div data-testid="page-midlife-clarity-guide">
      {/* HERO */}
      <section className="relative overflow-hidden pt-14 md:pt-20 pb-16 md:pb-20">
        <div
          className="absolute inset-0 -z-10"
          style={{ background: "linear-gradient(135deg, #F5EDE4 0%, #E3EBE4 100%)" }}
        />
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <SectionReveal>
            <div className="eyebrow mb-6 text-sage inline-flex items-center gap-2">
              <Sparkles size={12} strokeWidth={2} />
              Free Guide · 15 minutes
            </div>
            <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-ink leading-[1.02]">
              Something <em className="italic text-sage">Has</em> to Change.
            </h1>
            <p className="mt-4 font-serif-display text-2xl md:text-3xl text-ink-soft italic">
              A Midlife Clarity Guide
            </p>
            <p className="mt-8 max-w-2xl mx-auto text-lg md:text-xl text-ink-soft leading-relaxed">
              Sometimes nothing is obviously wrong, yet something about the way you are living no longer
              feels right.
            </p>
            <p className="mt-4 max-w-2xl mx-auto text-ink-soft leading-relaxed">
              You may still be functioning well and meeting all your responsibilities, keeping everything
              moving and appearing entirely capable from the outside. Yet underneath it all, there is a
              persistent sense that the life you are living no longer quite fits.
            </p>

            <div id="get-the-guide" className="mt-10 max-w-2xl mx-auto scroll-mt-24">
              <OptInForm />
            </div>
            <p className="mt-4 text-xs text-ink-soft/70 flex items-center justify-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <Download size={12} strokeWidth={1.5} />
                Free PDF · delivered to your inbox
              </span>
              <span className="text-ink-soft/40">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Clock size={12} strokeWidth={1.5} />
                Approx. 15 minutes to complete
              </span>
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* WHAT THE GUIDE HELPS YOU DO */}
      <section className="bg-cream-soft py-20 md:py-28 border-y border-hairline">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <SectionReveal>
            <div className="text-center mb-14">
              <div className="eyebrow mb-4 text-sage">What this free guide will help you do</div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-ink leading-tight">
                Pause. Notice. <em className="italic text-sage">Name it</em> — before rushing to fix anything.
              </h2>
            </div>
          </SectionReveal>

          <SectionReveal>
            <ul className="space-y-5">
              {WHAT_YOULL_DO.map((point, i) => (
                <li
                  key={i}
                  className="flex items-start gap-4 bg-white border border-hairline rounded-2xl p-6 md:p-7"
                  data-testid={`midlife-clarity-point-${i}`}
                >
                  <div className="w-9 h-9 rounded-full bg-sage/10 text-sage inline-flex items-center justify-center flex-shrink-0 font-serif-display text-lg">
                    {i + 1}
                  </div>
                  <p className="text-ink leading-relaxed text-[1.02rem]">{point}</p>
                </li>
              ))}
            </ul>
          </SectionReveal>

          <SectionReveal>
            <p className="mt-14 text-center font-serif-display text-2xl md:text-3xl text-sage italic max-w-xl mx-auto">
              You don&rsquo;t need a big, loud moment to listen to your inner self.
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-24 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <SectionReveal>
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gold/15 text-gold-dark mb-5">
                <Feather size={20} strokeWidth={1.5} />
              </div>
              <div className="eyebrow mb-3 text-gold-dark">Who this guide is for</div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight">
                For the moment when everything looks fine — but no longer feels right.
              </h2>
            </div>
          </SectionReveal>

          <SectionReveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {WHO_ITS_FOR.map((point, i) => (
                <div
                  key={i}
                  className="bg-cream-soft border border-hairline rounded-2xl p-6"
                  data-testid={`midlife-clarity-who-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <Check size={18} strokeWidth={2} className="text-sage flex-shrink-0 mt-1" />
                    <p className="text-ink leading-relaxed">{point}</p>
                  </div>
                </div>
              ))}
            </div>
          </SectionReveal>

          <SectionReveal>
            <div className="mt-14 text-center">
              <a
                href="#get-the-guide"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById("get-the-guide");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
                className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all"
                data-testid="midlife-clarity-scroll-form"
              >
                Get the Free Clarity Guide
                <ArrowRight size={16} strokeWidth={1.8} />
              </a>
              <p className="mt-4 text-xs text-ink-soft/70">
                No spam. Unsubscribe anytime. Free PDF, no strings.
              </p>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* SISTER RESOURCE */}
      <section className="bg-cream-soft py-20 md:py-24 border-t border-hairline">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <SectionReveal>
            <div className="eyebrow mb-4 text-sage">Also free</div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight">
              Feeling overwhelmed by the noise in your head?
            </h2>
            <p className="mt-6 max-w-xl mx-auto text-ink-soft leading-relaxed">
              The <em className="italic">Quiet the Noise Starter Kit</em> is a short workbook and 10-minute
              guided meditation for softening the inner critic and finding your first real moments of peace.
            </p>
            <Link
              to="/starter-kit"
              className="mt-8 inline-flex items-center gap-2 border border-sage text-sage hover:bg-sage hover:text-cream rounded-full px-7 py-3.5 font-medium tracking-wide transition-all"
              data-testid="midlife-clarity-starter-kit-cta"
            >
              Get the Quiet the Noise Starter Kit
              <ArrowRight size={16} strokeWidth={1.8} />
            </Link>
          </SectionReveal>
        </div>
      </section>

      <StickyCTA
        label="Get the Free Clarity Guide"
        href="#get-the-guide"
        testId="sticky-cta-midlife-clarity"
      />
    </div>
  );
}
